import { maxBy, round, sumBy } from 'lodash';

export const calculateCapitalAndLoanForFixedCapitalContribution = ({
  targetCommitment,
  capitalContributionPercentage,
}: {
  targetCommitment: number;
  capitalContributionPercentage?: number | null;
}) => {
  const capitalAmount =
    targetCommitment * ((capitalContributionPercentage ?? 0) / 100);
  const loanAmount = targetCommitment - capitalAmount;
  return { capitalAmount, loanAmount };
};

export const calculateCPCapitalFromPercentage = ({
  capitalContributionPercentage,
  nonCarryPartnerCapitalContributionTotalLcy,
}: {
  capitalContributionPercentage: number;
  nonCarryPartnerCapitalContributionTotalLcy: number;
}) => {
  const percentage = capitalContributionPercentage / 100;
  return round(
    (nonCarryPartnerCapitalContributionTotalLcy * percentage) /
      (1 - percentage),
    2,
  );
};

export const getCommitmentsWithFixedCapitalContribution = ({
  commitments,
  capitalContributionPercentage,
}: {
  commitments: Array<{
    id: string;
    investorName: string;
    totalCommitmentLcy: number;
    loanCommitmentLcy: number;
    capitalCommitmentLcy: number;
    isCarryPartnerCommitment?: boolean;
    isCapitalContributionRuleOverride?: boolean;
    isSelected: boolean;
  }>;
  capitalContributionPercentage?: number | null;
}) =>
  commitments.map((commitment) => {
    if (commitment.isCarryPartnerCommitment) {
      return commitment;
    }

    const { capitalAmount, loanAmount } =
      calculateCapitalAndLoanForFixedCapitalContribution({
        targetCommitment: commitment.totalCommitmentLcy,
        capitalContributionPercentage: capitalContributionPercentage ?? 0,
      });
    return {
      ...commitment,
      capitalCommitmentLcy: capitalAmount,
      loanCommitmentLcy: loanAmount,
    };
  });

export const getCommitmentsForManualCapitalContribution = ({
  commitments,
}: {
  commitments: Array<{
    id: string;
    investorName: string;
    totalCommitmentLcy: number;
    loanCommitmentLcy: number;
    capitalCommitmentLcy: number;
    isCarryPartnerCommitment?: boolean;
    isCapitalContributionRuleOverride?: boolean;
    isSelected: boolean;
  }>;
}) =>
  commitments.map((commitment) => {
    if (commitment.isCarryPartnerCommitment) {
      return commitment;
    }

    return {
      ...commitment,
      capitalCommitmentLcy: 0,
      loanCommitmentLcy: commitment.totalCommitmentLcy,
    };
  }) ?? [];

export const getCommitmentsWithProRataCapitalContribution = ({
  commitments,
  vehicleFixedCapitalAmount,
  capitalContributionPercentage,
}: {
  commitments: Array<{
    id: string;
    investorName: string;
    totalCommitmentLcy: number;
    loanCommitmentLcy: number;
    capitalCommitmentLcy: number;
    isCarryPartnerCommitment?: boolean;
    isCapitalContributionRuleOverride?: boolean;
    isSelected: boolean;
  }>;
  vehicleFixedCapitalAmount: number;
  capitalContributionPercentage?: number | null;
}) => {
  const fixedCapitalAmount = generateFixedCapitalAmount(
    vehicleFixedCapitalAmount,
    commitments,
    capitalContributionPercentage,
  );

  const commitmentsWithCarryPartnerCapitalCommitment =
    capitalContributionPercentage
      ? commitments.map((commitment) => {
          if (
            commitment.isCarryPartnerCommitment &&
            !commitment.isCapitalContributionRuleOverride
          ) {
            const capitalCommitmentLcy = round(
              fixedCapitalAmount * (capitalContributionPercentage / 100),
              2,
            );
            return {
              ...commitment,
              capitalCommitmentLcy,
              totalCommitmentLcy:
                capitalCommitmentLcy + (commitment.loanCommitmentLcy ?? 0),
            };
          }
          return commitment;
        })
      : commitments;

  const carryPartnerCommitment =
    commitmentsWithCarryPartnerCapitalCommitment.find(
      (c) => c.isCarryPartnerCommitment,
    );

  const selectedCommitments =
    commitmentsWithCarryPartnerCapitalCommitment.filter((c) => c.isSelected);

  // Come back to this when doing equalisations
  const sumOfAllClosedCommitments = 0;

  const sumOfAllClosingCommitments = sumBy(
    selectedCommitments,
    'totalCommitmentLcy',
  );

  const sumOfManualCapitalCommitments = sumBy(
    selectedCommitments.filter(
      (c) => c.isCapitalContributionRuleOverride || c.isCarryPartnerCommitment,
    ),
    'totalCommitmentLcy',
  );

  const manualCapitalAmountsInCurrentClose = sumBy(
    selectedCommitments.filter(
      (c) => c.isCapitalContributionRuleOverride && !c.isCarryPartnerCommitment,
    ),
    'capitalCommitmentLcy',
  );

  const commitmentsWithProRataCapitalContribution =
    commitmentsWithCarryPartnerCapitalCommitment.map((c) => {
      if (
        c.isCapitalContributionRuleOverride ||
        !c.isSelected ||
        c.isCarryPartnerCommitment
      ) {
        return c;
      }

      const capitalCommitmentLcy = c.isCapitalContributionRuleOverride
        ? c.capitalCommitmentLcy
        : calculateProRataCapitalContribution({
            totalCommitment: c.totalCommitmentLcy,
            sumOfAllClosedCommitments,
            sumOfAllClosingCommitments,
            sumOfManualCapitalCommitments,
            fixedCapitalAmount,
            carryPartnerAmountInFirstClose:
              carryPartnerCommitment?.capitalCommitmentLcy ?? 0,
            manualCapitalAmountsInCurrentClose,
          });

      const roundedCapitalCommitmentLcy = round(capitalCommitmentLcy, 2);
      const loanCommitmentLcy =
        roundedCapitalCommitmentLcy <= 0
          ? round(c.totalCommitmentLcy, 2)
          : round(c.totalCommitmentLcy - roundedCapitalCommitmentLcy, 2);

      return {
        ...c,
        capitalCommitmentLcy:
          roundedCapitalCommitmentLcy <= 0 ? 0 : roundedCapitalCommitmentLcy,
        loanCommitmentLcy: loanCommitmentLcy === 0 ? 0 : loanCommitmentLcy,
      };
    });

  const carryPartnerIsOverrideOrFixedCapitalContribution =
    commitments.some(
      (c) => c.isCarryPartnerCommitment && c.isCapitalContributionRuleOverride,
    ) || !capitalContributionPercentage;

  if (carryPartnerIsOverrideOrFixedCapitalContribution) {
    return commitmentsWithProRataCapitalContribution;
  }

  const carryPartnerAmountInFirstClose =
    carryPartnerCommitment?.capitalCommitmentLcy ?? 0;
  const availableCapitalAmount =
    fixedCapitalAmount -
    carryPartnerAmountInFirstClose -
    manualCapitalAmountsInCurrentClose;

  const adjustableCommitments =
    commitmentsWithProRataCapitalContribution.filter(
      (c) =>
        c.isSelected &&
        !c.isCapitalContributionRuleOverride &&
        !c.isCarryPartnerCommitment,
    );

  const sumOfProRataCapitalContributions = sumBy(
    adjustableCommitments,
    'capitalCommitmentLcy',
  );

  const difference = round(
    sumOfProRataCapitalContributions - availableCapitalAmount,
    2,
  );

  if (difference !== 0) {
    const biggestCommitment = maxBy(
      adjustableCommitments,
      'capitalCommitmentLcy',
    );
    return commitmentsWithProRataCapitalContribution.map((c) => {
      if (c.id === biggestCommitment?.id) {
        const newCapitalCommitmentLcy = round(
          c.capitalCommitmentLcy - difference,
          2,
        );
        return {
          ...c,
          capitalCommitmentLcy: newCapitalCommitmentLcy,
          loanCommitmentLcy: round(
            c.totalCommitmentLcy - newCapitalCommitmentLcy,
            2,
          ),
        };
      }
      return c;
    });
  } else {
    return commitmentsWithProRataCapitalContribution;
  }
};

const generateFixedCapitalAmount = (
  vehicleFixedCapitalAmount: number,
  commitments: Array<{
    capitalCommitmentLcy: number;
    totalCommitmentLcy: number;
    isCarryPartnerCommitment?: boolean;
    isCapitalContributionRuleOverride?: boolean;
    isSelected: boolean;
  }>,
  capitalContributionPercentage?: number | null,
) => {
  const carryPartnerIsOnlySelected = commitments
    .filter((c) => c.isSelected)
    .every((c) => c.isCarryPartnerCommitment);

  if (carryPartnerIsOnlySelected) {
    return vehicleFixedCapitalAmount;
  }

  const totalCommitmentLcyForCommitments = sumBy(
    commitments.filter((c) => c.isSelected && !c.isCarryPartnerCommitment),
    'totalCommitmentLcy',
  );

  if (totalCommitmentLcyForCommitments < vehicleFixedCapitalAmount) {
    const carryPartnerIsOverride = commitments.some(
      (c) => c.isCarryPartnerCommitment && c.isCapitalContributionRuleOverride,
    );
    if (carryPartnerIsOverride) {
      return totalCommitmentLcyForCommitments;
    }
    return (
      totalCommitmentLcyForCommitments /
      (1 - (capitalContributionPercentage ?? 0) / 100)
    );
  }

  const carryPartnerIsOverride = commitments.some(
    (c) => c.isCarryPartnerCommitment && c.isCapitalContributionRuleOverride,
  );

  if (!capitalContributionPercentage || carryPartnerIsOverride) {
    const sumOfCapitalContributions = sumBy(
      commitments.filter((c) => c.isSelected),
      'capitalCommitmentLcy',
    );
    return Math.max(vehicleFixedCapitalAmount, sumOfCapitalContributions);
  }

  const sumOfNonCPCapitalCommitments = sumBy(
    commitments.filter((c) => c.isSelected && !c.isCarryPartnerCommitment),
    'capitalCommitmentLcy',
  );

  // If there's a CP percentage, calculate the total fixed capital amount
  // where: nonCP = total * (1 - percentage), so total = nonCP / (1 - percentage)
  // This represents what the total would be if CP represents the given percentage.
  const calculatedFixedCapitalTotal =
    sumOfNonCPCapitalCommitments / (1 - capitalContributionPercentage / 100);

  // Return the maximum of vehicle fixed capital and calculated total
  // This ensures we use the vehicle value if it's higher, or the calculated value
  // if the commitments require more capital
  return Math.max(vehicleFixedCapitalAmount, calculatedFixedCapitalTotal);
};

/**
 * Calculates pro-rata capital contribution based on the formula:
 * (Total commitment / (Sum of all closed + currently being closed investor commitments - Sum of closed/closing commitments with manual capital input)) * (fixed capital amount in CL.1 - CP amount in CL.1 - any manual capital amounts)
 */
const calculateProRataCapitalContribution = ({
  totalCommitment,
  sumOfAllClosedCommitments,
  sumOfAllClosingCommitments,
  sumOfManualCapitalCommitments,
  fixedCapitalAmount,
  carryPartnerAmountInFirstClose,
  manualCapitalAmountsInCurrentClose,
}: {
  totalCommitment: number;
  sumOfAllClosedCommitments: number;
  sumOfAllClosingCommitments: number;
  sumOfManualCapitalCommitments: number;
  fixedCapitalAmount: number;
  carryPartnerAmountInFirstClose: number;
  manualCapitalAmountsInCurrentClose: number;
}) => {
  // Denominator: Sum of all closed + currently being closed investor commitments - Sum of closed/closing commitments with manual capital input
  const denominator =
    sumOfAllClosedCommitments +
    sumOfAllClosingCommitments -
    sumOfManualCapitalCommitments;

  // Avoid division by zero
  if (denominator === 0) {
    return 0;
  }

  // Numerator part 1: Total commitment / denominator
  const proRataFactor = totalCommitment / denominator;

  // Numerator part 2: fixed capital amount in CL.1 - CP amount in CL.1 - any manual capital amounts
  const availableCapitalAmount =
    fixedCapitalAmount -
    carryPartnerAmountInFirstClose -
    manualCapitalAmountsInCurrentClose;

  return proRataFactor * availableCapitalAmount;
};
